/**
 * A generation held across the sign-in reload.
 *
 * When an anonymous user hits the per-IP create cap we don't show an error — we
 * open the sign-in modal and resume their generation once they're authenticated
 * (signing in lifts the cap). `SignInModal` completes auth with a full
 * `window.location.reload()`, so the payload can't live in React state.
 *
 * sessionStorage, not localStorage: this is per-tab and dies with it, which is
 * the right lifetime for "the thing I was just doing". Same choice as the
 * `pendingChatUserMessage` handoff.
 */

export const PENDING_GENERATION_KEY = 'pendingUnlimitedGeneration';
export const PENDING_GENERATION_TTL_MS = 30 * 60 * 1000;

// Bumped when the stored shape changes, so an entry written by an older bundle
// is discarded rather than replayed as a malformed create.
const VERSION = 1;

const defaultStorage = () => (typeof sessionStorage === 'undefined' ? null : sessionStorage);

/**
 * Persist the payload of a create that was rate limited.
 *
 * Returns false when it couldn't be stored — a large `source_content` can
 * exceed the storage quota. The caller still opens the sign-in modal; only the
 * automatic resume is lost, and the user re-submits by hand.
 *
 * @returns {boolean} whether the payload was stored
 */
export function stashPendingGeneration(
  { createParams, followUpContext },
  storage = defaultStorage()
) {
  if (!storage) return false;
  try {
    storage.setItem(
      PENDING_GENERATION_KEY,
      JSON.stringify({ v: VERSION, createdAt: Date.now(), createParams, followUpContext })
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Read the pending generation and remove it in one step.
 *
 * Destructive by design: clearing before the caller can await anything is what
 * stops a StrictMode double-mount (or any re-render) from firing the same
 * generation twice. Returns null for a missing, malformed, outdated or expired
 * entry, clearing the key in every case.
 *
 * @returns {{createParams: object, followUpContext: unknown, createdAt: number} | null}
 */
export function takePendingGeneration(storage = defaultStorage(), now = Date.now()) {
  if (!storage) return null;

  const raw = storage.getItem(PENDING_GENERATION_KEY);
  if (!raw) return null;
  clearPendingGeneration(storage);

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (parsed?.v !== VERSION || !parsed.createParams) return null;
  if (now - parsed.createdAt > PENDING_GENERATION_TTL_MS) return null;

  return {
    createParams: parsed.createParams,
    followUpContext: parsed.followUpContext,
    createdAt: parsed.createdAt,
  };
}

export function clearPendingGeneration(storage = defaultStorage()) {
  if (!storage) return;
  try {
    storage.removeItem(PENDING_GENERATION_KEY);
  } catch {
    // Storage disabled mid-session; nothing to clean up.
  }
}
